import { appointmentPaymentRoute } from '@/services/appointmentPaymentRouting.js';
import { callFunctionRaw, resolveFunctionAccessToken } from "@/lib/callFunction";
import { resolveValidatedSession } from "@/lib/authSession.js";

const RETIRED_STRIPE_RUNTIME_MESSAGE =
  "This legacy payment action is unavailable. AfriMed™ now requires an explicitly configured supported payment provider.";

function retiredStripeRuntime() {
  throw new Error(RETIRED_STRIPE_RUNTIME_MESSAGE);
}

export async function callEdge(fn: string, body: any, opts: Record<string, unknown> = {}) {
  let accessToken =
    String(opts.accessToken || "").trim() ||
    (await resolveFunctionAccessToken({
      refreshIfMissing: true,
      forceRefresh: Boolean(opts.preferFreshSession),
    }));

  if (!accessToken) {
    const session = await resolveValidatedSession();
    accessToken = String(session?.access_token || "").trim();
  }

  if (!accessToken) {
    throw new Error("Session expired. Please sign in again.");
  }

  return callFunctionRaw(fn, body, {
    accessToken,
    timeoutMs: 12000,
    ...opts,
  });
}

export async function createPaymentIntent(params: {
  amount_cents: number;
  currency?: string;
  customer_email?: string;
  org_id?: string;
  user_id?: string;
  meta?: any;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function createSetupIntent(params: {
  customer_email?: string;
  stripeCustomerId?: string;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function listCustomerPaymentMethods(params: {
  customer_email?: string;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function emailInvoice(params: { to: string; invoice: any }) {
  return callEdge("email-invoice", params);
}

export async function createSponsorPaymentLink(params: {
  amount_cents: number;
  currency?: string;
  service_name?: string;
  doctor_name?: string;
  doctor_user_id?: string;
  doctor_stripe_account_id?: string;
  seller_name?: string;
  seller_user_id?: string;
  seller_role?: string;
  seller_stripe_account_id?: string;
  prescription_id?: string;
  pharmacy_owner_user_id?: string;
  patient_name?: string;
  patient_user_id?: string;
  sponsor_name?: string;
  sponsor_email?: string;
  sponsor_phone?: string;
  sponsor_country?: string;
  sponsor_relationship?: string;
  success_url?: string;
  cancel_url?: string;
  metadata?: Record<string, unknown>;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function syncSponsorPaymentSession(params: {
  session_id?: string;
  payment_link_id?: string;
  recovery_hint?: Record<string, unknown>;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function createSponsorPaymentRequest(params: {
  context_type: "appointment" | "prescription";
  context_id: string;
  patient_user_id?: string;
  amount_cents: number;
  currency?: string;
  metadata?: Record<string, unknown>;
  app_origin?: string;
}) {
  return callEdge("sponsor-payment", { ...params, action: "create" }, { timeoutMs: 30000 });
}

// These bearer-token actions intentionally use the opaque sponsor token rather
// than a patient session. They never accept raw card details.
export async function resolveSponsorPayment(params: { token: string }) {
  return callFunctionRaw("sponsor-payment", { ...params, action: "resolve" }, { timeoutMs: 30000 });
}

export async function initializeSponsorCardPayment(params: {
  token: string;
  sponsor_email: string;
  callback_url: string;
}) {
  return callFunctionRaw("sponsor-payment", { ...params, action: "initialize_card" }, { timeoutMs: 30000 });
}

export async function prepareSponsorTestBankTransfer(params: { token: string; sponsor_email: string }) {
  return callFunctionRaw("sponsor-payment", { ...params, action: "prepare_test_dva" }, { timeoutMs: 30000 });
}

export async function abandonSponsorPayment(params: { token: string }) {
  return callFunctionRaw("sponsor-payment", { ...params, action: "abandon" }, { timeoutMs: 30000 });
}

export async function syncAppointmentPayment(params: {
  appointment_id: string;
  payment_intent_id: string;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function reverseAppointmentPayment(params: {
  appointment_id: string;
  reason?: string;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function createPharmacyPaymentIntent(params: {
  prescription_id: string;
  pharmacy_owner_user_id: string;
  medicine_subtotal_cents: number;
  delivery_fee_cents?: number;
  currency?: string;
  customer_email?: string;
  save_payment_method?: boolean;
  meta?: Record<string, unknown>;
  accessToken?: string;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function claimPharmacyPrescription(params: {
  prescription_id: string;
  medicine_subtotal_cents: number;
  delivery_fee_cents?: number;
  currency?: string;
  fulfillment_mode?: string;
  meta?: Record<string, unknown>;
}) {
  return callEdge("claim-pharmacy-prescription", params, {
    timeoutMs: 15000,
    preferFreshSession: true,
  });
}

export async function listPharmacyBranches(params: {
  country?: string;
  lat?: number;
  lng?: number;
}) {
  return callEdge("list-pharmacy-branches", params, {
    timeoutMs: 12000,
  });
}

export async function requestPharmacyFulfillment(params: {
  prescription_id: string;
  requested_pharmacy_owner_user_id: string;
  fulfillment_mode?: string;
  lat?: number;
  lng?: number;
  meta?: Record<string, unknown>;
}) {
  return callEdge("request-pharmacy-fulfillment", params, {
    timeoutMs: 15000,
  });
}

export async function startPrescriptionFulfillment(params: {
  prescription_id: string;
}) {
  return callEdge("start-prescription-fulfillment", params, {
    timeoutMs: 15000,
  });
}

export async function updatePharmacyFulfillment(params: {
  fulfillment_id: string;
  action: "accept" | "reject" | "set_price" | "mark_ready_for_pickup";
  price?: number;
  medicine_subtotal?: number;
  delivery_fee?: number;
  delivery_quote_confirmed?: boolean;
  currency?: string;
}) {
  return callEdge("update-pharmacy-fulfillment", params, {
    timeoutMs: 15000,
  });
}

export async function syncPharmacyPayment(params: {
  prescription_id: string;
  payment_intent_id: string;
  accessToken?: string;
}) {
  void params;
  return retiredStripeRuntime();
}

export async function initializePaystackPharmacyPayment(params: {
  prescription_id: string;
  pharmacy_owner_user_id: string;
  medicine_subtotal_cents: number;
  delivery_fee_cents?: number;
  currency?: string;
  customer_email?: string;
  callback_url: string;
  meta?: Record<string, unknown>;
  accessToken?: string;
}) {
  return callEdge("paystack-pharmacy-checkout", { ...params, action: "initialize" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}

export async function verifyPaystackPharmacyPayment(params: {
  prescription_id: string;
  reference: string;
  accessToken?: string;
}) {
  return callEdge("paystack-pharmacy-checkout", { ...params, action: "verify" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}

export async function initializePaystackAppointmentPayment(params: {
  locale?: string;
  clinician_user_id: string;
  service_id: string;
  starts_at: string;
  idempotency_key: string;
  payment_method?: "card" | "instant_eft" | "capitec_pay" | "qr" | "apple_pay" | "google_pay" | "samsung_pay";
  capitec_identifier?: { key: "CELLPHONE" | "IDNUMBER" | "ACCOUNTNUMBER"; value: string };
  consultation_type?: string;
  practice_id?: string;
  service_name?: string;
  care_subject_type?: "self" | "dependant";
  care_subject_dependant_id?: string;
  accessToken?: string;
}) {
  const route = await appointmentPaymentRoute(params.service_id);
  if (route.selected) {
    if (!route.available) throw new Error('Payment is currently unavailable.');
    return callEdge('peach-appointment-checkout', { ...params, payment_method: params.payment_method || 'card', action: 'initialize' }, { timeoutMs: 30000, accessToken: String(params.accessToken || '').trim() });
  }
  return callEdge((params.payment_method || "card") === "card" ? "clinician-settlement" : "paystack-appointment-checkout", { ...params, payment_method: params.payment_method || "card", action: "initialize" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}

export async function verifyPaystackAppointmentPayment(params: {
  attempt_id?: string;
  reference?: string;
  accessToken?: string;
}) {
  return callEdge("paystack-appointment-checkout", { ...params, action: "verify" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}
export async function initializeMtnMomoAppointmentPayment(params: { clinician_user_id: string; service_id: string; starts_at: string; idempotency_key: string; payer_msisdn: string; consultation_type?: string; care_subject_type?: "self" | "dependant"; care_subject_dependant_id?: string; accessToken?: string; }) {
  return callEdge("mtn-momo-appointment-checkout", { ...params, action: "initialize" }, { timeoutMs: 30000, accessToken: String(params?.accessToken || "").trim() });
}
export async function verifyMtnMomoAppointmentPayment(params: { attempt_id: string; accessToken?: string }) {
  return callEdge("mtn-momo-appointment-checkout", { ...params, action: "verify" }, { timeoutMs: 30000, accessToken: String(params?.accessToken || "").trim() });
}
export async function recoverMtnMomoAppointmentPayment(params: { clinician_user_id: string; service_id: string; starts_at: string; accessToken?: string }) {
  return callEdge("mtn-momo-appointment-checkout", { ...params, action: "recover" }, { timeoutMs: 30000, accessToken: String(params?.accessToken || "").trim() });
}

export async function listPendingMtnMomoAppointmentPayments(params: { accessToken?: string } = {}) {
  return callEdge("mtn-momo-appointment-checkout", { action: "pending" }, { timeoutMs: 30000, accessToken: String(params?.accessToken || "").trim() });
}

// DEV/Test-only rail probe. It shares the same hosted Paystack adapter as the
// pharmacy flow, but deliberately has no appointment or fulfilment mutation.
export async function initializePaystackPaymentHarness(params: {
  callback_url: string;
  customer_email?: string;
  accessToken?: string;
}) {
  return callEdge("paystack-pharmacy-checkout", { ...params, action: "test_initialize" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}

export async function verifyPaystackPaymentHarness(params: { reference: string; accessToken?: string }) {
  return callEdge("paystack-pharmacy-checkout", { ...params, action: "test_verify" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}

// DEV/Test-only bank-transfer probe. It creates or reuses a Paystack Test
// Bank virtual account for the signed-in tester; it has no appointment,
// prescription, notification, or stored-payment-method effect.
export async function preparePaystackTestBankTransfer(params: {
  customer_email?: string;
  first_name?: string;
  last_name?: string;
  accessToken?: string;
}) {
  return callEdge("paystack-pharmacy-checkout", { ...params, action: "test_dva" }, {
    timeoutMs: 30000,
    accessToken: String(params?.accessToken || "").trim(),
  });
}

export async function reversePharmacyPayment(params: {
  prescription_id: string;
  reason?: string;
}) {
  void params;
  return retiredStripeRuntime();
}
